import { HomeOutlined } from '@mui/icons-material';
import {
  AppDrawerMenu,
  AppName,
  AutocompleteItem,
  getIsValidURLFormat,
  HomeAppBar,
  SearchBar,
} from '@prismamedia/one-components';
import { isDefined } from '../../utils/isDefined';
import { format } from 'date-fns';
import React, { FC, useEffect } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import {
  ArticleFormat,
  ArticleOrderingSearchInput,
  ArticleStatus,
  BrandKey,
  CategoryFormat,
  CategoryStatus,
} from '../../__generated__/queries-web';
import { useSearchArticlesGetter } from '../../apollo/queries/searchArticles.web.graphql';
import { useQuery } from '@apollo/client';
import { GET_PAGE_CATEGORIES } from '../../pages/Pages/apollo/queries/categories.web.graphql';
import { paths } from '../../routing/Router/paths';
import { auth } from '../../utils/auth';
import { AdvancedSearchParams } from '../../utils/formatArticleSearchInput';
import {
  useGlobalFormat,
  useGlobalSearchParams,
  useGlobalStatus,
} from '../../utils/globalState';
import { BrandSwitch } from '../BrandSwitch';
import { AdvancedSearch } from './AdvancedSearch';
import { BoneLink, menuLinks } from './confLinks';

interface AppBarProps {
  app: AppName;
  tabs?: { label: string; status: ArticleStatus }[];
  setSearchParams?: (params: AdvancedSearchParams) => void;
  searchParams?: AdvancedSearchParams;
  articleFormat?: ArticleFormat;
  brandKey?: BrandKey;
  status: ArticleStatus;
  setBrandKey?: (brandKey: BrandKey | undefined) => void;
  setStatus?: (status: ArticleStatus) => void;
}

interface OneDrawerMenuProps {
  currentBrand?: BrandKey;
  app: AppName;
  isOpen?: boolean;
  onLogOff?: () => any;
}

const getLinkUrl = (link: BoneLink, brand?: BrandKey) => {
  const { href, hrefWithoutBrand } = link;
  return href && brand
    ? href.replace('{BRAND}', brand)
    : hrefWithoutBrand || href;
};

const shouldDisableURL = (link: BoneLink, brand?: BrandKey) => {
  const { href, hrefWithoutBrand, onClick } = link;

  if (onClick) return false;
  if (!href && !hrefWithoutBrand) return true;

  const shouldHaveBrandKey = Boolean(
    href && href.indexOf('{BRAND}') > -1 && !hrefWithoutBrand,
  );
  return Boolean(shouldHaveBrandKey && !brand);
};

const baseUrl = `${window.location.protocol}//${window.location.host}`;

const matchURL = (target?: string) =>
  target && document.location.href.indexOf(target) > -1;

export const OneDrawerMenu = ({ currentBrand, app }: OneDrawerMenuProps) => {
  const [, setStatus] = useGlobalStatus();
  const [, setSearchParams] = useGlobalSearchParams();
  const [, setFormat] = useGlobalFormat();
  const [open, setOpen] = React.useState<boolean>(true);
  const history = useHistory();

  // Get level 2 categories for drawer menu
  const { data: categoriesData } = useQuery(GET_PAGE_CATEGORIES, {
    variables: {
      first: 100,
      where: {
        brandKey_in: currentBrand ? [currentBrand] : [],
        format: CategoryFormat.Category,
        level: 2,
        status_in: [
          CategoryStatus.Published,
          CategoryStatus.Scheduled,
          CategoryStatus.Draft,
          CategoryStatus.Deleted,
        ],
      },
    },
    skip: !currentBrand,
  });

  const drawerMenuCategories = categoriesData?.categories || [];

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStatus(ArticleStatus.Published);
        setSearchParams({});
      });
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <AppDrawerMenu
      appName={app}
      disconnectUser={auth.logout}
      topSection={(drawerMenuCategories?.length && drawerMenuCategories[0].id
        ? [
            ...menuLinks,
            {
              label: 'Home page',
              icon: <HomeOutlined fontSize="medium" />,
              onClick: () => {
                setFormat(CategoryFormat.Category);
                history.push(
                  `/${currentBrand}/page/${CategoryFormat.Category.toLowerCase()}/edit/${
                    drawerMenuCategories[0].id
                  }#general-configuration`,
                );
              },
            },
          ]
        : menuLinks
      ).map((link) => ({
        ...link,
        href: getLinkUrl(link, currentBrand),
        disabled: shouldDisableURL(link, currentBrand),
        selected: !!matchURL(getLinkUrl(link, currentBrand)),
        onClick: () => {
          if (isDefined(link.openBlank)) {
            setOpen(link.openBlank);
          }
          if (link.onClick) {
            link.onClick();
          }
        },
      }))}
      overriddenAppUrls={{
        [AppName.Authors]: `${baseUrl}/#/authors/list`,
        [AppName.Board]: config.FRONT_ONE_BOARD,
        [AppName.Newsletter]: config.FRONT_ONE_NEWSLETTER,
        [AppName.People]: config.FRONT_ONE_PEOPLE,
        [AppName.Photo]: config.FRONT_ONE_PHOTO,
        [AppName.Recipe]: config.FRONT_ONE_RECIPE,
        [AppName.Story]: config.FRONT_ONE_TOPIC,
        [AppName.Video]: `${baseUrl}/#/video/list`,
      }}
    />
  );
};

export const AppBarOne: FC<AppBarProps> = ({
  app,
  tabs,
  setSearchParams,
  searchParams,
  articleFormat,
  brandKey,
  status,
  setBrandKey,
  setStatus,
}) => {
  const history = useHistory();
  const searchUsers = useSearchArticlesGetter();

  const fetchAutocompleteList = async (
    search: string,
  ): Promise<AutocompleteItem[] | undefined> => {
    const { data, errors } = await searchUsers({
      first: 10,
      skip: 0,
      orderBy: [ArticleOrderingSearchInput.publishedAt_DESC],
      ...(search &&
        getIsValidURLFormat(search) && {
          query: search,
        }),
      where: {
        format: articleFormat,
        status: ArticleStatus.Published,
        brandKey,
        ...(search &&
          !getIsValidURLFormat(search) && {
            title: search,
          }),
      },
    });

    if (errors?.length) {
      throw errors[0];
    }

    return data?.searchArticles?.map(
      ({ id, title, publishedAt, brandKey: articleBrand }) => ({
        id,
        label: title || 'NO_TITLE',
        secondaryLabel: publishedAt
          ? format(new Date(publishedAt), 'd MMM')
          : undefined,
        additionnals: { brandKey: articleBrand },
      }),
    );
  };

  return (
    <HomeAppBar
      currentUser={auth.user}
      disconnectUser={auth.logout}
      appDrawerMenu={<OneDrawerMenu app={app} currentBrand={brandKey} />}
      brandMenu={<BrandSwitch brandKey={brandKey} setBrandKey={setBrandKey} />}
      searchBar={
        searchParams && setSearchParams ? (
          <SearchBar
            advancedSearch={
              brandKey
                ? {
                    component: AdvancedSearch,
                    props: { brandKey, articleFormat },
                  }
                : undefined
            }
            autocomplete={{
              fetchList: fetchAutocompleteList,
              onSelect: ({ id, additionnals }) => {
                const articleLink =
                  articleFormat === ArticleFormat.Slideshow
                    ? generatePath(paths.SLIDESHOW_EDIT, {
                        id: id || '',
                      })
                    : generatePath(paths.ARTICLE_EDIT, {
                        brandKey: additionnals?.brandKey,
                        articleType:
                          articleFormat === ArticleFormat.Rich
                            ? 'article'
                            : 'video',
                        id: id || undefined,
                      });
                articleLink && history.push(articleLink);
              },
            }}
            placeholder={`Rechercher dans One ${app}`}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        ) : undefined
      }
      tabs={tabs?.map(({ status: tabStatus, label }) => ({
        id: tabStatus,
        label,
        selected: status === tabStatus,
      }))}
      onTabSelect={(tab) => {
        setStatus?.(tab.id as ArticleStatus);
      }}
    />
  );
};
